import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { useToast } from "../hooks/use-toast";
import {
  GripVertical, ChevronUp, ChevronDown, Trash2, Plus, Save, Code2, LifeBuoy, ChevronRight,
} from "lucide-react";
import { cn } from "../lib/utils";

function newRow() {
  return {
    id: `tmp-${Math.random().toString(36).slice(2, 9)}`,
    name: "",
    hours: "",
    lines: [""],
    note: "",
    display_order: 100,
  };
}

function renderHtmlPreview(intro, rows) {
  const rowsHtml = rows
    .map(
      (r) => `
  <tr>
    <td><strong>${escapeHtml(r.name)}</strong></td>
    <td>${escapeHtml(r.hours).replace(/\\*\\*([^*]+)\\*\\*/g, "<strong>$1</strong>")}</td>
    <td>${(r.lines || [])
      .filter(Boolean)
      .map((l) => escapeHtml(l))
      .join("<br/>")}${r.note ? `<br/><em>${escapeHtml(r.note)}</em>` : ""}</td>
  </tr>`
    )
    .join("");
  return `<section>
  ${intro ? `<p>${escapeHtml(intro)}</p>` : ""}
  <table>
    <thead><tr><th>Label</th><th>Hours</th><th>Contacts</th></tr></thead>
    <tbody>${rowsHtml}
    </tbody>
  </table>
</section>`;
}

function escapeHtml(s) {
  return String(s || "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

export default function EscalationEditor() {
  const { toast } = useToast();
  const [intro, setIntro] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showHtml, setShowHtml] = useState(false);
  const [collapsed, setCollapsed] = useState(true); // collapsed by default

  const load = useCallback(async () => {
    try {
      const [{ data: contacts }, { data: s }] = await Promise.all([
        api.get("/support-contacts"),
        api.get("/settings"),
      ]);
      setRows(
        (contacts || []).map((c, i) => ({
          id: c.id || `tmp-${i}`,
          name: c.name || "",
          hours: c.hours || "",
          lines: (c.lines && c.lines.length ? c.lines : [c.email, c.phone].filter(Boolean)) || [""],
          note: c.note || "",
          display_order: c.display_order ?? (i + 1) * 10,
        }))
      );
      setIntro(s?.escalation_intro || "Reach out to the following contacts for technical support.");
    } catch (e) {
      toast({ title: "Failed to load", variant: "destructive" });
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[idx], next[j]] = [next[j], next[idx]];
    setRows(next);
  };
  const patchRow = (idx, patch) => {
    const next = [...rows]; next[idx] = { ...next[idx], ...patch }; setRows(next);
  };
  const patchLine = (idx, li, val) => {
    const next = [...rows];
    const lines = [...(next[idx].lines || [])];
    lines[li] = val;
    next[idx] = { ...next[idx], lines };
    setRows(next);
  };
  const removeLine = (idx, li) => {
    const next = [...rows];
    const lines = (next[idx].lines || []).filter((_, i) => i !== li);
    next[idx] = { ...next[idx], lines: lines.length ? lines : [""] };
    setRows(next);
  };
  const addLine = (idx) => {
    const next = [...rows];
    next[idx] = { ...next[idx], lines: [...(next[idx].lines || []), ""] };
    setRows(next);
  };
  const removeRow = (idx) => setRows(rows.filter((_, i) => i !== idx));
  const addRow = () => setRows([...rows, newRow()]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        intro,
        contacts: rows.map((r, i) => ({
          id: r.id?.startsWith("tmp-") ? null : r.id,
          name: r.name.trim(),
          hours: r.hours,
          lines: (r.lines || []).map((s) => (s || "").trim()).filter(Boolean),
          note: r.note?.trim() || null,
          display_order: (i + 1) * 10,
        })).filter((c) => c.name),
      };
      await api.put("/admin/support-contacts", payload);
      toast({ title: "Escalation contacts saved" });
      await load();
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || "Try again.", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const htmlPreview = renderHtmlPreview(intro, rows);

  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-5">
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center gap-2 text-left group"
        aria-expanded={!collapsed}
      >
        <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
          <LifeBuoy className="w-4 h-4 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-[15px] font-semibold group-hover:text-primary transition-colors">Escalation — Partners & Staff</h3>
          <p className="text-[12px] text-muted-foreground">Renders as a clean 3-column table on the Dashboard. Rows below map straight to label · hours · contacts.</p>
        </div>
        <span className="text-muted-foreground group-hover:text-foreground transition-colors">
          {collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </span>
      </button>

      {!collapsed && (
        <>
          <div>
            <Label>Intro paragraph (optional)</Label>
            <Textarea value={intro} onChange={(e) => setIntro(e.target.value)} rows={2} className="mt-1" />
          </div>

      <div className="space-y-4">
        {rows.map((r, idx) => (
          <div key={r.id} className="rounded-lg border border-border p-3 sm:p-4">
            <div className="flex items-start gap-2">
              <div className="text-muted-foreground pt-2"><GripVertical className="w-4 h-4" /></div>
              <div className="flex-1 min-w-0">
                <Input
                  value={r.name}
                  onChange={(e) => patchRow(idx, { name: e.target.value })}
                  placeholder="Label (e.g. Technical Support)"
                  className="font-semibold"
                />
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={() => move(idx, -1)} disabled={idx === 0} className="h-8 w-8"><ChevronUp className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => move(idx, 1)} disabled={idx === rows.length - 1} className="h-8 w-8"><ChevronDown className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => removeRow(idx)} className="h-8 w-8 text-destructive hover:text-destructive"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[12px]">Availability / hours</Label>
                <Textarea
                  value={r.hours}
                  onChange={(e) => patchRow(idx, { hours: e.target.value })}
                  rows={3}
                  className="mt-1 font-mono text-[12.5px]"
                  placeholder={"Mon\u2013Fri, 6:30 am \u2013 5:30 pm MDT\nor **24/7/365**"}
                />
                <div className="mt-1 text-[11px] text-muted-foreground">
                  Wrap any text in <code className="text-[11px] rounded bg-muted px-1">**double asterisks**</code> to bold it — <em>24/7</em> tokens auto-bold.
                </div>
              </div>
              <div>
                <Label className="text-[12px]">Contact lines</Label>
                <div className="mt-1 space-y-2">
                  {(r.lines || [""]).map((ln, li) => (
                    <div key={li} className="flex gap-1.5">
                      <Input
                        value={ln}
                        onChange={(e) => patchLine(idx, li, e.target.value)}
                        placeholder="email@company.com or (800) 555-0123"
                      />
                      <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-destructive" onClick={() => removeLine(idx, li)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => addLine(idx)} className="h-8 gap-1 border-dashed">
                    <Plus className="w-3.5 h-3.5" /> Add contact
                  </Button>
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground leading-snug">
                  Emails become <code>mailto:</code> links; phone numbers become <code>tel:</code> links automatically.
                </div>
              </div>
            </div>

            <div className="mt-3">
              <Label className="text-[12px]">Footnote (optional)</Label>
              <Input value={r.note || ""} onChange={(e) => patchRow(idx, { note: e.target.value })} placeholder="e.g. Keith — Mobile" className="mt-1" />
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <Button variant="outline" onClick={addRow} className="gap-1.5"><Plus className="w-4 h-4" /> Add contact row</Button>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => setShowHtml((v) => !v)} className="gap-1.5 text-muted-foreground">
            <Code2 className="w-4 h-4" /> {showHtml ? "Hide HTML" : "Show HTML"}
          </Button>
          <Button onClick={save} disabled={busy} className="gap-2"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Save escalation"}</Button>
        </div>
      </div>

      {showHtml && (
        <pre className={cn("rounded-lg border border-border bg-muted/50 p-3 text-[11.5px] leading-snug overflow-auto max-h-64 tabler-scroll font-mono")}>
{htmlPreview}
        </pre>
      )}
        </>
      )}
    </div>
  );
}
